<template>
  <div ref="commentContainer" class="comment_container">
    <div
      v-for="comment in comments"
      :key="comment.comment_id"
      :class="[
        'comment',
        `type_${comment.content_type}`,
        comment.content_type === CONTENT_TYPE.SUPPORTER_RANK_UP
          ? `supporter_${getSupporterRank(comment)}`
          : '',
      ]"
    >
      <img :src="comment.icon_url" class="comment_icon" />
      <img
        v-if="isShowSupporterIcon(comment)"
        :src="getRankIconImagePath(comment)"
        class="supporter_icon"
      />

      <img
        v-if="isShowMissionIcon(comment)"
        src="/img/comment/mission.png"
        class="mission_icon"
      />
      <img
        v-if="isShowModeratorIcon(comment)"
        src="/img/comment/moderator.png"
        class="moderator_icon"
      />

      <div class="comment_body">
        <p v-if="isShowNickname(comment.content_type)">{{ comment.nickname }}</p>
        <p class="comment_bold">{{ comment.text }}</p>
      </div>
    </div>
    <div @click="scrollToBottom">
      <img
        v-if="showButton"
        src="/img/comment/move_to_new_message.png"
        class="moveto_new_message_icon"
      />
      <img
        v-if="showButton && hasNewMessage"
        src="/img/comment/badge.png"
        class="button_badge"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator'
import Config from '@/config/vue.config'

type RevisedSupporterBadge = {
  version: string
  supporterLevel: number
  supporterRank: number
}

interface CommentOperation {
  operator_message: string
}

interface Comment {
  comment_id: string
  content: string
  content_type: number
  created_at: string
  deco: number
  hidden: boolean
  icon_url: string
  is_beginner_mission_in_progress: boolean
  is_resent: boolean
  is_self: boolean
  media_id: number
  nickname: string
  show_moderator_icon: boolean
  supporter_level: number
  revised_supporter_badge: RevisedSupporterBadge
  vlive_id: string
  text: string
  comment_operation: CommentOperation
}

const CONTENT_TYPE = {
  // チャットメッセージ系
  STREAMER: 0,
  VIEWER: 1,
  LIKE: 2,
  GIFT: 3,
  FOLLOW: 4,
  SHARE: 5,
  SOFT: 6,
  WEARING: 7,
  ENTER: 8,
  SYSTEM: 9,
  AVATARGIFT: 10,
  MODERATOR_ADD: 11,
  MODERATOR_REMOVE: 12,
  MODERATION_LOG: 13,
  SUPPORTER_LEVEL_UP: 14,
  SUPPORTER_RANK_UP: 15,

  // 非チャットメッセージ系
  CLOSE: 100, // 配信者が配信終了を処理した時のコマンドコメント
  HIDE: 101,
  COMMENT_BANNED: 102, // ユーザーのコメントをBANする (そのユーザーの全てのコメントを削除扱いで表示)
  COMMENT_UNBANNED: 103,
}

const WS_TIMEOUT = 50 * 60 * 1000; // 50分

@Component
export default class extends Vue {
  // Relay
  private readonly CONTENT_TYPE = CONTENT_TYPE

  connection_timeout: number= 0;
  comments: Comment[] = []
  created() {
    this.receiveComment()
    this.monitorScroll()

    // 全体用のスタイル変数などを設定。
    document.body.style.setProperty('--comment-scale', '1.5')
    document.body.style.backgroundColor = 'black'
    ;(document.querySelector('#app')! as HTMLElement).style.overflow = 'scroll'
  }

  async monitorScroll() {
    // スクロール位置を監視する
    const scrollTarget = document.querySelector('#app')!
    scrollTarget.addEventListener('scroll', () => {
      if (this.isAtBottom()) {
        this.showButton = false
        this.hasNewMessage = false
      } else {
        this.showButton = true
      }
    })
  }

  waitForRequestAnimationFrame(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        resolve()
      })
    })
  }

  showButton: boolean = false
  hasNewMessage: boolean = false

  async receiveComment() {
    const regex = /(\d+)\.([a-zA-Z0-9]+)$/
    const match = this.$route.params.mediaIdAndHash.match(regex)

    if (match == null) {
      return
    }

    const mdeiaId = match[1]

    let url = Config.commentServerUrl + '/?media_id='
    url = url + mdeiaId

    clearTimeout(this.connection_timeout);

    let ws = new WebSocket(url)

    ws.onmessage = (event) => {
      let commentData = JSON.parse(event.data)
      this.addComment(commentData)
    }

    ws.onerror = (event) => {
      try {
        ws.close()
      } catch {
        // 閉じるのに失敗しただけなので何もしない
      }

      // 再接続
      this.receiveComment()
    }


    // サーバーのTCPタイムアウトがあり、うまく検知できないことがあるので、その時間より短い時間(50分)で再接続する
    this.connection_timeout = window.setTimeout(() => {
      try {
        ws.close()
      } catch {
        // 閉じるのに失敗しただけなので何もしない
      }
      // 再接続
      this.receiveComment()
    }, WS_TIMEOUT);

  }

  addComment(commentData: Comment) {
    let comment = this.setCommentText(commentData)
    // comments内で、comment_idが一致するものがあるかどうか
    let index = this.comments.findIndex(
      (oldComment) => comment.comment_id === oldComment.comment_id
    )
    if (index === -1) {
      this.comments.push(comment)
      if (this.isAtBottom()) {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } else {
        this.hasNewMessage = true
      }
    } else {
      var oldData = this.comments[index]
      this.comments[index] = comment

      // なぜかアイコンが入ってなくて表示されないことがあるので、その場合は古いデータを使う
      if (!comment.icon_url) {
        comment.icon_url = oldData.icon_url
      }
      this.$set(this.comments, index, comment)
    }
  }

  /**
   * サポーターアイコンを表示するかどうか
   */
  private isShowSupporterIcon(comment: Comment) {
    return this.getSupporterRank(comment) > 0
  }

  /**
   * ミッションアイコンを表示するかどうか
   */
  private isShowMissionIcon(comment: Comment) {
    return comment.is_beginner_mission_in_progress
  }

  /**
   * Moderatorアイコンを表示するかどうか
   */
  private isShowModeratorIcon(comment: Comment) {
    return comment.show_moderator_icon
  }

  /**
   * 名前を表示するかどうか
   * @param contentType コメントのタイプ
   */
  private isShowNickname(contentType: number): boolean {
    // content_typeが特定の値のときのみ、nicknameを表示
    const showContentTypes = [
      CONTENT_TYPE.STREAMER,
      CONTENT_TYPE.VIEWER,
      CONTENT_TYPE.GIFT,
      CONTENT_TYPE.AVATARGIFT,
    ]
    return showContentTypes.includes(contentType)
  }

  /**
   * サポーターバッジの画像のパスを取得
   */
  private getRankIconImagePath(comment: Comment): string {
    const rank = this.getSupporterRank(comment)
    return '/img/comment/supporter_rank' + rank + '.png'
  }

  /**
   * サポーターのランクを取得
   */
  private getSupporterRank(comment: Comment) {
    return comment.revised_supporter_badge?.supporterRank ?? 0
  }

  setCommentText(comment: Comment): Comment {
    if (comment.comment_operation) {
      comment.text = comment.comment_operation.operator_message
    } else if (
      comment.content_type === CONTENT_TYPE.GIFT ||
      comment.content_type === CONTENT_TYPE.AVATARGIFT
    ) {
      comment.text = comment.nickname + ' ' + comment.content.split(',')[3]
    } else {
      comment.text = comment.content
    }

    return comment
  }

  isAtBottom(): boolean {
    let scrollTarget = document.querySelector('#app')!
    let isBottom =
      scrollTarget.scrollTop +
        scrollTarget.clientHeight -
        scrollTarget.scrollHeight >
      -5

    return isBottom
  }

  scrollToBottom() {
    let scrollTarget = document.querySelector('#app')!
    scrollTarget.scrollTop = scrollTarget.scrollHeight
  }
}
</script>

<style scoped>
.comment {
  font-family: 'SF Pro Display';
  font-size: calc(1.4vw * var(--comment-scale));
  font-style: normal;
  line-height: calc(2vw * var(--comment-scale));
  letter-spacing: calc(0.025vw * var(--comment-scale));
  color: rgba(255, 255, 255, 0.4);
  border-radius: calc(0.6vw * var(--comment-scale));
  padding: calc(0.4vw * var(--comment-scale));
  margin-bottom: calc(0.4vw * var(--comment-scale));
  font-weight: 400;
  word-break: break-all;
  display: flex;
}

.comment_bold {
  color: rgba(255, 255, 255, 1);
  font-weight: 700;
}

.comment p {
  padding: calc(0.4vw * var(--comment-scale));
}

.supporter_icon {
  vertical-align: top;
  margin-top: calc(0.6vw * var(--comment-scale));
  margin-left: calc(0.8vw * var(--comment-scale));
  width: calc(3.2vw * var(--comment-scale));
  height: calc(1.6vw * var(--comment-scale));
}

.mission_icon {
  vertical-align: top;
  margin-top: calc(0.6vw * var(--comment-scale));
  margin-left: calc(0.8vw * var(--comment-scale));
  width: calc(1.2vw * var(--comment-scale));
  height: calc(1.2vw * var(--comment-scale));
}

.moderator_icon {
  vertical-align: top;
  margin-top: calc(0.6vw * var(--comment-scale));
  margin-left: calc(0.8vw * var(--comment-scale));
  width: calc(1.2vw * var(--comment-scale));
  height: calc(1.2vw * var(--comment-scale));
}

.supporter_1 {
  background: linear-gradient(
    90deg,
    rgba(255, 185, 27, 0.8) 0%,
    rgba(255, 138, 0, 0.8) 100%
  );
}
.supporter_2 {
  background: linear-gradient(
    90deg,
    rgba(250, 164, 96, 0.8) 0%,
    rgba(255, 71, 0, 0.8) 100%
  );
}
.supporter_3 {
  background: linear-gradient(
    90deg,
    rgba(251, 146, 125, 0.8) 0%,
    rgba(234, 58, 68, 0.8) 100%
  );
}
.supporter_4 {
  background: linear-gradient(
    90deg,
    rgba(251, 151, 179, 0.8) 0%,
    rgba(255, 24, 140, 0.8) 100%
  );
}
.supporter_5 {
  background: linear-gradient(
    90deg,
    rgba(238, 126, 177, 0.8) 0%,
    rgba(255, 0, 61, 0.8) 100%
  );
}
.supporter_6 {
  background: linear-gradient(
    90deg,
    rgba(255, 152, 78, 0.8) 0%,
    rgba(236, 24, 255, 0.8) 100%
  );
}
.supporter_7 {
  background: linear-gradient(
    90deg,
    rgba(255, 141, 249, 0.8) 0%,
    rgba(124, 77, 255, 0.8) 100%
  );
}

.comment.type_3 {
  background: rgba(255, 0, 61, 0.8);
  border-radius: calc(0.6vw * var(--comment-scale));
}

.comment.type_3 {
  background: rgba(255, 0, 61, 0.8);
}

.comment_body {
  display: inline-block;
  /* width: calc(100% - 3.6vw * var(--comment-scale)); */
  margin-left: calc(0.8vw * var(--comment-scale));
}

.comment_icon {
  vertical-align: top;
  width: calc(2.8vw * var(--comment-scale));
  height: calc(2.8vw * var(--comment-scale));
  border-radius: calc(1.4vw * var(--comment-scale));
}

.comment_container {
  background: black;
  padding: calc(0.8vw * var(--comment-scale));
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: end;
}

.moveto_new_message_icon {
  position: absolute;
  right: calc(1.6vw * var(--comment-scale));
  bottom: calc(0.8vw * var(--comment-scale));
  width: calc(3.2vw * var(--comment-scale));
  height: calc(3.2vw * var(--comment-scale));
}

.button_badge {
  position: absolute;
  right: calc(1.8vw * var(--comment-scale));
  bottom: calc(3.27vw * var(--comment-scale));
  width: calc(0.53vw * var(--comment-scale));
  height: calc(0.53vw * var(--comment-scale));
}
</style>
